import { useState } from "react";

import { calibrateStride } from "../domain/energy";
import { useKaya } from "../store/kayaStore";
import { ActionBar } from "./ActionBar";
import { Sheet } from "./Sheet";

/**
 * Lets a logged walk carry a distance the user vouches for.
 *
 * A walk's own `km` is steps × stride, so it can never teach the stride — that would be
 * circular. A distance the user *knows* (five laps of a 400 m track, a route measured once) is
 * the treadmill case without the treadmill: known distance, counted steps. (ADR-027)
 *
 * Moved out of the Treadmill page unchanged. Render it only while open.
 */
export function DeclareKmSheet({ ds, index, onClose }: { ds: string; index: number; onClose: () => void }) {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const walk = S.logs[ds]?.walks?.[index];
  const [kmText, setKmText] = useState(walk?.kmDeclared ? walk.kmDeclared.toFixed(2) : "");

  const save = () => {
    const w = S.logs[ds]?.walks?.[index];
    if (!w) return;
    const km = Number(kmText);
    if (!Number.isFinite(km) || km <= 0 || km > 50) {
      showToast("That does not look like a distance in km");
      return;
    }
    onClose();
    update((d) => {
      const target = d.logs[ds]?.walks?.[index];
      if (!target) return;
      // A walk teaches the stride once. Correcting the figure later does not count it again —
      // the average would otherwise weight one walk as two.
      const first = typeof target.kmDeclared !== "number";
      target.kmDeclared = km;
      // Already measured: calibrate now, because the sync pass only revisits walks with no
      // measurement yet. Not yet measured: the pass calibrates when the steps arrive.
      if (first && typeof target.measuredSteps === "number") {
        const cal = calibrateStride(d, km, target.measuredSteps);
        if (cal) {
          d.profile.strideM = cal.strideM;
          d.profile.strideSamples = cal.strideSamples;
        }
      }
    });
    showToast(
      typeof w.measuredSteps === "number"
        ? "Distance noted — your stride learned from it"
        : "Distance noted — it teaches your stride once the steps arrive"
    );
  };

  return (
    <Sheet
      title="How far did you walk?"
      sub="Only if you know it — five laps of a 400 m track is 2.0 km."
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          primary={{ label: "Save distance", onClick: save }}
          secondary={{ label: "Cancel", onClick: onClose }}
        />
      }
    >
      <div className="field">
        <label htmlFor="walk-km">Distance (km)</label>
        <input
          id="walk-km"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={kmText}
          onChange={(e) => setKmText(e.target.value)}
        />
      </div>
      <p className="train-field-note">
        Paired with your phone&rsquo;s step count for the walk, it teaches Kaya your stride.
      </p>
    </Sheet>
  );
}
